import {
    DoubleInput,
    FormSection,
    InputContainer,
    LeftSide,
    RightSide,
    SafetyMessage,
    StyledInput,
    SubmitButton,
    Title,
    TitleRow,
    WidgetContainer,
    WidgetRow,
    StyledInputWrapper,
    InputErrorMessage,
    RecallErrorMessage,
    DisclaimerContainer,
    LoadingSpinner,
} from "./StyledComponents";
import Warning from "../../assets/SVGComponents/Warning";
import StateDropdown from "./StateDropdown";
import { useEffect, useState } from "react";
import LicensePlateInput from "./LicensePlateInput/LicensePlateInput";
import RecallModal from "./RecallModal";
import { TestRecallData } from "./TestRecallData";
import formatPhoneNumber from "../../assets/store/formatPhoneNumber";
import { formatText } from "../../assets/store/formatText";

function RecallWidget({ divId, client, oem, dataAttributes, rowData }) {
    const { require_email_in_modal, enable_recall_lookup, state, tealium_namespace } = client;
    const [selectedState, setSelectedState] = useState(state);
    const [license, setLicense] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [recallInfo, setRecallInfo] = useState(null);
    const [submissionError, setSubmissionError] = useState(null);
    const [sendingInfo, setSendingInfo] = useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = useState(false);
    const [fetchState, setFetchState] = useState("error");
    const [commStatus, setCommStatus] = useState("start");
    const [width, setWidth] = useState(
        document.getElementsByTagName("html")[0].clientWidth
    );
    const [warningMargin, setWarningMargin] = useState("-30px 0");
    const [nameEngaged, setNameEngaged] = useState(false);
    const [phoneEngaged, setPhoneEngaged] = useState(false);
    const [emailEngaged, setEmailEngaged] = useState(false);
    const { isMobile } = window?.window?.deviceDetect?.getWindowData() || {};
    // input validation styles
    const [inputStyles, setInputStyles] = useState({
        name: null,
        email: null,
        phone: "",
        license: null,
        state: null,
    });

    const { recall_widget_theme } = rowData || { recall_widget_theme: null };

    const {
        primary_button_color,
        primary_hover_button_color,
        primary_button_text_color,
        primary_hover_button_text_color,
        letter_spacing,
        line_height,
        text_transform,
        border_radius,
        border_color,
    } = oem || {
        primary_button_color: "black",
        primary_hover_button_color: "white",
        primary_button_text_color: "white",
        primary_hover_button_text_color: "black",
    };

    let recall_theme = {
        submitButtonTextColor: recall_widget_theme
            ? recall_widget_theme.submit_button_text_color
            : primary_button_text_color,
        submitButtonBackgroundColor: recall_widget_theme
            ? recall_widget_theme.submit_button_background_color
            : primary_button_color,
        submitButtonHoverTextColor: recall_widget_theme
            ? recall_widget_theme.submit_button_hover_text_color
            : primary_hover_button_text_color,
        submitButtonHoverBackgroundColor: recall_widget_theme
            ? recall_widget_theme.submit_button_hover_background_color
            : primary_hover_button_color,
        includeWarningIcons: recall_widget_theme
            ? recall_widget_theme.include_warning_icons
            : true,
        inputBorderRadius: recall_widget_theme
            ? recall_widget_theme.input_border_radius
            : border_radius,
        licensePlateInputBackground: recall_widget_theme
            ? recall_widget_theme.license_plate_input_background_color
            : "white",
        licensePlateInputBorderColor: recall_widget_theme
            ? recall_widget_theme.license_plate_border_color
            : "white",
        submitButtonBorderColor: recall_widget_theme
            ? recall_widget_theme.submit_button_border_color
            : border_color,
        textTransform: recall_widget_theme
            ? recall_widget_theme.text_transformation
            : text_transform ?? "unset",
    };

    function handleResize() {
        setWidth(document.getElementsByTagName("html")[0].clientWidth);
    }
    const fireTealiumCTAInteraction = async (tealium_namespace) => {
        const eventPayload = {
            event: "cta_interaction",
            event_action_result: "redirect",
            event_owner: "trade pending",
            product_name: "service offers",
            element_text: formatText(rowData, "Check For Recall"),
            element_type: "item_details",
            element_subtype: "cta_button",
            event_action: "click",
            tealium_event: "do.cta_interaction"
        };
        const tealiumNamespace = window[tealium_namespace]
        if (tealiumNamespace) {
            tealiumNamespace.link(eventPayload);
        }
    }

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        if (tealium_namespace === "rohrmantag") {
            fireTealiumCTAInteraction(tealium_namespace)
        }
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (width < 312) {
            setWarningMargin("-45px -75px");
        } else if (width < 650) {
            setWarningMargin("-45px -60px");
        } else {
            setWarningMargin("-45px 0");
        }
    }, [width]);

    const couponId = null;
    const hostId = `${window.DriveServiceSpecials?.host_name?.id}` ?? "1";

    const validateFields = () => {
        const validLicense = license && license !== "";
        const validState = selectedState && selectedState !== "";
        const validName = name && name !== "";
        const validPhone =
            phone &&
            phone !== "" &&
            phone.match(
                /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/
            );
        const validEmail = require_email_in_modal
            ? email && email !== "" && email.indexOf("@") !== -1
            : true;
        const invalidFields = [
            validName ? null : "name",
            validPhone ? null : "phone",
            validLicense ? null : "license",
            validEmail ? null : "email",
            validState ? null : "state",
        ];
        return {
            ok: Boolean(validName && validLicense && validPhone && validState), // validEmail
            invalidFields,
        };
    };

    const onSubmitCustomTealiumAnalytics = async (data, tealium_namespace) => {
        const eventPayload = {
            event: "form_submit",
            form_name: "open_recalls",
            customer_email_address: data?.email || "",
            customer_phone_number: data?.phone || "",
            customer_name: data?.name || "",
            event_owner: "trade pending",
            product_name: "Service Offers",
            element_subtype: "cta_button",
            event_action: "click",
            tealium_event: "do.form_submission"
        };
        const tealiumNamespace = window[tealium_namespace]
        if (tealiumNamespace) {
            tealiumNamespace.link(eventPayload);
        }
    }


    const sendCustomTealiumEvent = (eventPayload) => {
        const { tealium_namespace } = eventPayload;
        const tracker = window[tealium_namespace];
        if (tracker) {
            eventPayload.form_name = "open_recalls";
            tracker.link(eventPayload);
        }
    };

    const onBlurEmail = (email) => {
        if (tealium_namespace !== "rohrmantag") return;
        if (email) {
            const emailRegex = /^\S+@\S+$/i;
            if (emailRegex.test(email)) {
                sendCustomTealiumEvent({
                    tealium_event: "customer_email_scrape",
                    tealium_namespace,
                    customer_email_address: email,
                });
            }
        }
    }

    const onBlurPhone = (phone) => {
        if (tealium_namespace !== "rohrmantag") return;
        if (phone) {
            const phoneDigits = phone.replace(/\D/g, '');
            if (phoneDigits.length === 10) {
                sendCustomTealiumEvent({
                    tealium_event: "customer_phone_scrape",
                    tealium_namespace,
                    customer_phone_number: phone,
                });
            }
        }
    };

    function submitForm(e) {
        e.preventDefault();
        if (license === "FXDOPSTEST") return setRecallInfo(TestRecallData);

        const isValid = validateFields();

        if (!isValid.ok) {
            setInputStyles({
                // reset these styles before applying again.
                name: null,
                email: null,
                phone: "",
                license: null,
                state: null,
            });
            isValid.invalidFields.forEach((field) => {
                setInputStyles((prevState) => {
                    return {
                        ...prevState,
                        [field]: { outline: "2px solid red" },
                    };
                });
            });
            return;
        } else {
            setFetchState("success");
            setCommStatus("crm_update");
        }

        // because of setState asynchronicity validation cannot be reliably checked after onChanges...
        // need to simulate click on element with all data attributes after successful submission so ga events get data properly
        const btn = document.createElement("button");
        btn.className = "FODV5GARecallClickEvent";
        btn.setAttribute("data-state", selectedState);
        for (let attribute in dataAttributes) {
            btn.setAttribute(attribute, dataAttributes[attribute]); // adds data attributes to this new  element
        }

        document.body.append(btn);
        btn.click(); // triggers click event only when submitting form is successful
        document.body.removeChild(btn);

        const body = JSON.stringify({
            name,
            email,
            phone,
            coupon_widget_id: couponId,
            host_name_id: hostId,
            drive_service_div_id: divId,
            license_plate: license,
            state: selectedState,
        });
        if (tealium_namespace === "rohrmantag") {
            const analyticData = { name, email, phone }
            onSubmitCustomTealiumAnalytics(analyticData, tealium_namespace);
        }
        setSendingInfo(true);
        fetch(process.env.REACT_APP_RECALL_API, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: body,
            signal: AbortSignal.timeout(10000),
        })
            .then((res) => {
                setSendingInfo(false);
                if (res.ok) return res.json();
                else {
                    if (res.status === 429) {
                        setSubmissionError(
                            "You've exceeded the maximum number of requests. Please try again later."
                        );
                        setDisableSubmitButton(true);
                    } else if (res.status === 404)
                        setSubmissionError("Vehicle data not found.");
                    else if (res.state === 400)
                        setSubmissionError(
                            "Invalid license plate. Please check and try again."
                        );
                    else
                        setSubmissionError(
                            "Something went wrong, please try again."
                        );
                    return Promise.reject(res);
                }
            })
            .catch((err) => {
                if (err.name === "AbortError")
                    setSubmissionError(
                        "Request timed out. Please try again later."
                    );
            })
            .then((recallData) => {
                // because of setState asynchronicity validation cannot be reliably checked after onChanges...
                // need to simulate click on element with all data attributes after successful submission so ga events get data properly
                const btn = document.createElement("button");
                btn.className = "FODV5GARecallClickEvent";
                for (let attribute in dataAttributes) {
                    btn.setAttribute(attribute, dataAttributes[attribute]); // adds data attributes to this new  element
                }
                document.body.append(btn);
                btn.click(); // triggers click event only when submitting form is successful
                document.body.removeChild(btn);
                setSendingInfo(false);
                setRecallInfo(recallData);
                // Reset asc events
                setFetchState("error");
                setCommStatus("start");
            });
    }

    let resetInputs = () => {
        setLicense("");
        setSelectedState("");
        setName("");
        setPhone("");
        setEmail("");
        setInputStyles({
            name: null,
            email: null,
            phone: "",
            license: null,
            state: null,
        });
    };

    let closeModalFunction = () => {
        resetInputs();
        setRecallInfo(null);
    };

    let recallModalProps = {
        closeModal: closeModalFunction,
        name,
        phone,
        email,
        recallDataProp: recallInfo,
    };

    let recallModal = <RecallModal {...recallModalProps} />;

    return (
        <>
            {recallInfo ? recallModal : null}
            {enable_recall_lookup ? (
                <WidgetRow
                    className="FODRecallWidgetRow"
                    style={{ fontFamily: "inherit" }}
                >
                    <WidgetContainer className="FODRecallWidgetContainer">
                        <TitleRow className="FODRecallWidgetTitleRow">
                            {recall_theme.includeWarningIcons ? (
                                <Warning
                                    className="FODRecallWidgetWarning1"
                                    svgIconSize={"50px"}
                                    style={{
                                        marginRight: 20,
                                    }}
                                />
                            ) : null}
                            <Title
                                className="FODRecallWidgetTitle"
                                letterSpacing={letter_spacing}
                                textTransform={recall_theme.textTransform}
                            >
                                {formatText(
                                    rowData,
                                    "Check Your Vehicle for Open Recalls",
                                    false
                                )}
                            </Title>
                            {recall_theme.includeWarningIcons ? (
                                <Warning
                                    className="FODRecallWidgetWarning1"
                                    svgIconSize={"50px"}
                                    style={{
                                        marginLeft: 20,
                                    }}
                                />
                            ) : null}
                        </TitleRow>
                        <FormSection onSubmit={submitForm}>
                            <LeftSide
                                className="FODRecallWidgetLeftSide"
                                style={
                                    !inputStyles.state && !inputStyles.license
                                        ? { gap: "1.5rem" }
                                        : null
                                }
                            >
                                <LicensePlateInput
                                    className="FODRecallWidgetLicensePlateInput"
                                    license={license}
                                    setLicense={setLicense}
                                    state={selectedState}
                                    isDealerWallet={false}
                                    rowData={rowData}
                                    sendingInfo={sendingInfo}
                                    theme={recall_theme}
                                    dataAscformname="open_recalls"
                                    dataAscformtype="recall"
                                />
                                {(() => {
                                    if (
                                        !inputStyles.state &&
                                        !inputStyles.license
                                    )
                                        return null;
                                    let error = "";
                                    if (
                                        inputStyles.state &&
                                        inputStyles.license
                                    ) {
                                        error =
                                            "Please select a state and enter your license plate number.";
                                    } else if (inputStyles.state) {
                                        error = "Please select a state.";
                                    } else {
                                        error =
                                            "Please enter your license plate number.";
                                    }
                                    return (
                                        <RecallErrorMessage>
                                            {error}
                                        </RecallErrorMessage>
                                    );
                                })()}
                                <StateDropdown
                                    className="FODRecallWidgetStateDropdown"
                                    selectedState={selectedState}
                                    setSelectedState={setSelectedState}
                                    rowData={rowData}
                                    oem={oem}
                                    sendingInfo={sendingInfo}
                                    theme={recall_theme}
                                    dataAscformname="open_recalls"
                                    dataAscformtype="recall"
                                />
                            </LeftSide>
                            <RightSide className="FODRecallWidgetRightSide">
                                <SafetyMessage
                                    mobile={isMobile}
                                    letterSpacing={letter_spacing}
                                    textTransform={recall_theme.textTransform}
                                    className="FODRecallWidgetSafetyMessage"
                                >
                                    {formatText(
                                        rowData,
                                        "Your Safety Is Our Priority",
                                        false
                                    )}
                                </SafetyMessage>
                                {submissionError ? (
                                    <p
                                        style={{
                                            color: "red",
                                            textAlign: "center",
                                        }}
                                    >
                                        {submissionError}
                                    </p>
                                ) : null}
                                <InputContainer className="FODRecallWidgetInputContainer">
                                    <StyledInputWrapper>
                                        <StyledInput
                                            className="FODRecallWidgetNameInput"
                                            type="text"
                                            placeholder={
                                                formatText(rowData, "Name") +
                                                "*"
                                            }
                                            id="fod-recall-rabbit-widget-name-input"
                                            style={{
                                                ...inputStyles.name,
                                                borderRadius:
                                                    recall_theme.inputBorderRadius,
                                                textTransform:
                                                    recall_theme.textTransform,
                                                letterSpacing: letter_spacing,
                                            }}
                                            value={name}
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                            disabled={sendingInfo}
                                            data-ascformname={"open_recalls"}
                                            data-ascformtype={"recall"}
                                            onBlur={() => {
                                                if (!nameEngaged) {
                                                    setNameEngaged(true);
                                                    window.dataLayer.push({
                                                        event: "gtm.blur.service-offers",
                                                        text: formatText(rowData, "Name")
                                                    });
                                                }
                                            }}
                                        />
                                        {inputStyles.name && (
                                            <InputErrorMessage>
                                                Please enter a valid value.
                                            </InputErrorMessage>
                                        )}
                                    </StyledInputWrapper>

                                    <DoubleInput>
                                        <StyledInputWrapper>
                                            <StyledInput
                                                className="FODRecallWidgetNumberInput"
                                                type="tel"
                                                placeholder={
                                                    formatText(
                                                        rowData,
                                                        "Phone"
                                                    ) + "*"
                                                }
                                                id="fod-recall-rabbit-widget-phone-input"
                                                style={{
                                                    ...inputStyles.phone,
                                                    flex: "0 1 50%",
                                                    borderRadius:
                                                        recall_theme.inputBorderRadius,
                                                    textTransform:
                                                        recall_theme.textTransform,
                                                    letterSpacing:
                                                        letter_spacing,
                                                }}
                                                value={phone}
                                                onChange={(e) => {
                                                    let phoneNumber =
                                                        e.target.value;
                                                    // only format phone number after number has been entered and if the user has not formatted themselves
                                                    if (
                                                        phoneNumber.length ===
                                                            10 &&
                                                        phoneNumber[0] !==
                                                            "(" &&
                                                        !phoneNumber[4] !== ")"
                                                    )
                                                        phoneNumber =
                                                            formatPhoneNumber(
                                                                phoneNumber
                                                            );
                                                    setPhone(
                                                        phoneNumber.trim()
                                                    );
                                                }}
                                                disabled={sendingInfo}
                                                data-ascformname={"open_recalls"}
                                                data-ascformtype={"recall"}
                                                onBlur={(event) => {
                                                    onBlurPhone(event.target.value)
                                                    if (!phoneEngaged) {
                                                        setPhoneEngaged(true);
                                                        window.dataLayer.push({
                                                            event: 'gtm.blur.service-offers',
                                                            text: formatText(rowData, "Phone")
                                                        });
                                                    }
                                                }}
                                            />
                                            {inputStyles.phone && (
                                                <InputErrorMessage>
                                                    Please enter a valid phone
                                                    number.
                                                </InputErrorMessage>
                                            )}
                                        </StyledInputWrapper>
                                        <StyledInputWrapper>
                                            <StyledInput
                                                className="FODRecallWidgetEmailInput"
                                                type="email"
                                                placeholder={
                                                    require_email_in_modal
                                                        ? formatText(
                                                              rowData,
                                                              "Email"
                                                          ) + "*"
                                                        : formatText(
                                                              rowData,
                                                              "Email"
                                                          )
                                                }
                                                id="fod-recall-rabbit-widget-email-input"
                                                style={{
                                                    ...inputStyles.email,
                                                    flex: "0 1 50%",
                                                    borderRadius:
                                                        recall_theme.inputBorderRadius,
                                                    textTransform:
                                                        recall_theme.textTransform,
                                                    letterSpacing:
                                                        letter_spacing,
                                                }}
                                                value={email}
                                                onChange={(e) =>
                                                    setEmail(
                                                        e.target.value.trim()
                                                    )
                                                }
                                                disabled={sendingInfo}
                                                data-ascformname={"open_recalls"}
                                                data-ascformtype={"recall"}
                                                onBlur={(event) => {
                                                    onBlurEmail(event.target.value)
                                                    if (!emailEngaged) {
                                                        setEmailEngaged(true);
                                                        window.dataLayer.push({
                                                            event: 'gtm.blur.service-offers',
                                                            text: formatText(rowData, "Email")
                                                        });
                                                    }
                                                }}
                                            />
                                            {inputStyles.email && (
                                                <InputErrorMessage>
                                                    Please enter a valid email.
                                                </InputErrorMessage>
                                            )}
                                        </StyledInputWrapper>
                                    </DoubleInput>
                                    <SubmitButton
                                        data-asccommstatus={commStatus}
                                        data-ascformname="open_recalls"
                                        data-ascformtype="recall"
                                        className="FODGASubmitRecallRabbitButton"
                                        data-testid="submit_recall"
                                        data-ascasceventact={fetchState}
                                        type="submit"
                                        disabled={
                                            disableSubmitButton || sendingInfo
                                                ? true
                                                : false
                                        }
                                        style={{
                                            borderRadius:
                                                recall_theme.inputBorderRadius ??
                                                "30px",
                                            textTransform:
                                                recall_theme.textTransform ??
                                                "unset",
                                            letterSpacing: letter_spacing,
                                        }}
                                        bgColor={
                                            recall_theme.submitButtonBackgroundColor ??
                                            "white"
                                        }
                                        textColor={
                                            recall_theme.submitButtonTextColor ??
                                            "black"
                                        }
                                        borderColor={
                                            recall_theme.submitButtonBorderColor ??
                                            "unset"
                                        }
                                        sendingInfo={sendingInfo}
                                        onMouseOver={(e) => {
                                            if (
                                                disableSubmitButton ||
                                                sendingInfo
                                            )
                                                return;
                                            e.target.style.backgroundColor =
                                                recall_theme.submitButtonHoverBackgroundColor;
                                            e.target.style.color =
                                                recall_theme.submitButtonHoverTextColor;
                                        }}
                                        onMouseLeave={(e) => {
                                            if (
                                                disableSubmitButton ||
                                                sendingInfo
                                            )
                                                return;
                                            e.target.style.backgroundColor =
                                                recall_theme.submitButtonBackgroundColor;
                                            e.target.style.color =
                                                recall_theme.submitButtonTextColor;
                                        }}
                                    >
                                        {sendingInfo
                                            ? formatText(rowData, "Loading") +
                                              "..."
                                            : formatText(
                                                  rowData,
                                                  "Check For Recall"
                                              )}
                                        {sendingInfo ? (
                                            <LoadingSpinner
                                                color={
                                                    recall_theme.submitButtonTextColor ??
                                                    "white"
                                                }
                                            />
                                        ) : null}
                                    </SubmitButton>
                                </InputContainer>
                            </RightSide>
                        </FormSection>
                        <DisclaimerContainer
                            className="FODRecallDisclaimer"
                            lineHeight={line_height}
                        >
                            {formatText(
                                rowData,
                                "*No spam guarantee. By submitting this form, you agree to receive automated marketing text messages from",
                                false
                            )}
                            {` ${client.name} `}
                            {formatText(
                                rowData,
                                "at the mobile number used when signing up. Msg and data rates apply. See",
                                false
                            ) + " "}
                            <a
                                href="https://www.fixedopsdigital.com/privacy-policy/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                {formatText(rowData, "Terms & Conditions")}.
                            </a>
                        </DisclaimerContainer>
                    </WidgetContainer>
                </WidgetRow>
            ) : null}
        </>
    );
}

export default RecallWidget;
